import React, { useMemo, useState } from "react";
import "./CreateGameModal.css";

function CreateGameModal({
  leagueType,
  teams,
  players,
  handleCreateGames,
  setIsModalOpen,
  savedLocations,
  setShowCreateLocationModal,
  setShowEditLocationsModal,
  setNewSavedLocationForm,
}) {
  const isIndividual = leagueType === "individual";
  const sideALabel = isIndividual ? "Player 1" : "Home Team";
  const sideBLabel = isIndividual ? "Player 2" : "Away Team";
  const optionLabel = isIndividual ? "Select a player" : "Select a team";
  const participants = isIndividual ? players : teams;

  const createRow = (defaults = {}) => ({
    rowKey: `${Date.now()}-${Math.random()}`,
    team1_id: "",
    team1_partner_id: "",
    team2_id: "",
    team2_partner_id: "",
    time: "",
    location: defaults.location || "",
    location_url: defaults.location_url || "",
    locationSearchOpen: false,
  });

  const createInitialRows = () => {
    return Array.from({ length: 5 }, () => createRow());
  };
  const [bulkForm, setBulkForm] = useState({
    date: "",
    location: "",
    location_url: "",
    locationSearchOpen: false,
  });

  const [gameRows, setGameRows] = useState(createInitialRows());
  const [isSaving, setIsSaving] = useState(false);
  const [matchFormat, setMatchFormat] = useState("singles");

  const getParticipantId = (participant) => {
    return isIndividual
      ? participant.id
      : participant.seasons_leagues_teams_id;
  };

  const getParticipantName = (participant) => {
    if (isIndividual) {
      const player = participant.player || participant;

      return (
        player?.name ||
        player?.full_name ||
        `${player?.firstName || ""} ${player?.lastName || ""}`.trim() ||
        `${player?.first_name || ""} ${player?.last_name || ""}`.trim() ||
        player?.username ||
        "Unnamed Player"
      );
    }

    return participant.name || participant.team?.name || "Unnamed Team";
  };

  const participantNameById = useMemo(() => {
    const map = {};

    participants.forEach((participant) => {
      const id = getParticipantId(participant);
      map[String(id)] = getParticipantName(participant);
    });

    return map;
  }, [participants, isIndividual]);

  const updateBulkForm = (field, value) => {
    setBulkForm((prevBulkForm) => {
      const previousValue = prevBulkForm[field];

      if (field === "location") {
        setGameRows((prevRows) =>
          prevRows.map((row) => ({
            ...row,
            location:
              !row.location || row.location === previousValue
                ? value
                : row.location,
          }))
        );
      }

      if (field === "location_url") {
        setGameRows((prevRows) =>
          prevRows.map((row) => ({
            ...row,
            location_url:
              !row.location_url || row.location_url === previousValue
                ? value
                : row.location_url,
          }))
        );
      }

      return {
        ...prevBulkForm,
        [field]: value,
      };
    });
  };

  const updateRow = (rowKey, field, value) => {
    setGameRows((prev) =>
      prev.map((row) =>
        row.rowKey === rowKey ? { ...row, [field]: value } : row
      )
    );
  };

  const addAnotherGame = () => {
    setGameRows((prev) => [
      ...prev,
      createRow({
        location: bulkForm.location,
        location_url: bulkForm.location_url,
      }),
    ]);
  };

  const removeGameRow = (rowKey) => {
    setGameRows((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((row) => row.rowKey !== rowKey);
    });
  };

  const getLocationMatches = (value) => {
    const search = String(value || "").trim().toLowerCase();

    if (!search) return [];

    return savedLocations.filter((loc) =>
      String(loc.name || "").toLowerCase().startsWith(search)
    );
  };

  const handleSelectBulkLocation = (loc) => {
    const locationName = loc.name || "";
    const locationUrl = loc.location_url || "";

    setBulkForm((prevBulkForm) => {
      const previousLocation = prevBulkForm.location;
      const previousLocationUrl = prevBulkForm.location_url;

      setGameRows((prevRows) =>
        prevRows.map((row) => ({
          ...row,
          location:
            !row.location || row.location === previousLocation
              ? locationName
              : row.location,
          location_url:
            !row.location_url || row.location_url === previousLocationUrl
              ? locationUrl
              : row.location_url,
        }))
      );

      return {
        ...prevBulkForm,
        location: locationName,
        location_url: locationUrl,
        locationSearchOpen: false,
      };
    });
  };

  const handleSelectRowLocation = (rowKey, loc) => {
    setGameRows((prev) =>
      prev.map((row) =>
        row.rowKey === rowKey
          ? {
            ...row,
            location: loc.name || "",
            location_url: loc.location_url || "",
            locationSearchOpen: false,
          }
          : row
      )
    );
  };

  const validateGames = () => {
    if (!bulkForm.date) {
      alert("Date is required.");
      return false;
    }

    const filledRows = gameRows.filter(
      (row) => row.time || row.team1_id || row.team2_id
    );

    if (filledRows.length === 0) {
      alert("Please enter at least one game.");
      return false;
    }

    for (let i = 0; i < filledRows.length; i += 1) {
      const row = filledRows[i];


      if (!row.team1_id) {
        alert(`${sideALabel} is required for Game ${i + 1}.`);
        return false;
      }

      if (!row.team2_id) {
        alert(`${sideBLabel} is required for Game ${i + 1}.`);
        return false;
      }
      if (isIndividual && matchFormat === "doubles") {
        if (!row.team1_partner_id) {
          alert(`Player 1 Partner is required for Game ${i + 1}.`);
          return false;
        }

        if (!row.team2_partner_id) {
          alert(`Player 2 Partner is required for Game ${i + 1}.`);
          return false;
        }
      }

      if (isIndividual && matchFormat === "doubles") {
        const selectedPlayers = [
          row.team1_id,
          row.team1_partner_id,
          row.team2_id,
          row.team2_partner_id,
        ].map(String);

        const uniquePlayers = new Set(selectedPlayers);

        if (uniquePlayers.size !== 4) {
          alert(`All 4 players must be different for Game ${i + 1}.`);
          return false;
        }
      }
      if (String(row.team1_id) === String(row.team2_id)) {
        alert(
          isIndividual
            ? `Players must be different for Game ${i + 1}.`
            : `Teams must be different for Game ${i + 1}.`
        );
        return false;
      }
    }

    return true;
  };
  const handleSaveGames = async () => {
    if (!validateGames()) return;

    const filledRows = gameRows.filter(
      (row) => row.time || row.team1_id || row.team2_id
    );

    const gamesToCreate = filledRows.map((row) => ({
      team1_id: row.team1_id,
      team2_id: row.team2_id,

      team1_partner_id:
        isIndividual && matchFormat === "doubles"
          ? row.team1_partner_id
          : null,

      team2_partner_id:
        isIndividual && matchFormat === "doubles"
          ? row.team2_partner_id
          : null,

      match_format:
        isIndividual ? matchFormat : null,

      date: bulkForm.date,
      time: row.time || null,
      location: row.location || bulkForm.location,
      location_url: row.location_url || bulkForm.location_url,
    }));

    setIsSaving(true);

    await handleCreateGames(gamesToCreate);

    setIsSaving(false);
  };

  const bulkLocationMatches = getLocationMatches(bulkForm.location);

  return (
    <div className="createGameModalPage-overlay">
      <div className="createGameModalPage-modal">
        <div className="createGameModalPage-header">
          <div>
            <p className="createGameModalPage-eyebrow">Create Schedule</p>
            <h3 className="createGameModalPage-title">Add Games</h3>
            <p className="createGameModalPage-subtitle">
              Choose a date, then add one or multiple games. Use the default
              location or override it per game.
            </p>
          </div>

          <button
            type="button"
            className="createGameModalPage-closeBtn"
            onClick={() => setIsModalOpen(false)}
          >
            ×
          </button>
        </div>

        <div className="createGameModalPage-bulkCard">
          <div className="createGameModalPage-sectionHeader">
            <h4>Game Date & Default Location</h4>
            <p>
              All games below will use this date. Location is optional and can be changed per row.
            </p>
          </div>

          <div className="createGameModalPage-bulkGrid">
            <label className="createGameModalPage-field">
              <span>Game Date</span>
              <input
                type="date"
                value={bulkForm.date}
                onChange={(e) => updateBulkForm("date", e.target.value)}
              />
            </label>

            <div className="createGameModalPage-field createGameModalPage-locationField">
              <span>Default Location</span>

              <div className="createGameModalPage-locationInputWrap">
                <input
                  type="text"
                  value={bulkForm.location}
                  onChange={(e) => {
                    updateBulkForm("location", e.target.value);
                    updateBulkForm("locationSearchOpen", true);
                  }}
                  onFocus={() => updateBulkForm("locationSearchOpen", true)}
                  autoComplete="off"
                />

                {bulkForm.locationSearchOpen && bulkLocationMatches.length > 0 && (
                  <div className="createGameModalPage-locationDropdown">
                    {bulkLocationMatches.map((loc) => (
                      <button
                        type="button"
                        key={loc.id}
                        className="createGameModalPage-locationDropdownItem"
                        onClick={() => handleSelectBulkLocation(loc)}
                      >
                        {loc.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <label className="createGameModalPage-field">
              <span>Default Location URL</span>
              <input
                type="text"
                value={bulkForm.location_url}
                onChange={(e) => updateBulkForm("location_url", e.target.value)}
              />
            </label>
          </div>

          <div className="createGameModalPage-locationLinks">
            <button
              type="button"
              className="createGameModalPage-linkBtn"
              onClick={() => {
                setShowCreateLocationModal(true);
                setNewSavedLocationForm({
                  name: bulkForm.location || "",
                  location_url: bulkForm.location_url || "",
                });
              }}
            >
              + Create a new location
            </button>

            <button
              type="button"
              className="createGameModalPage-linkBtn"
              onClick={() => setShowEditLocationsModal(true)}
            >
              Edit current locations
            </button>
          </div>
        </div>
        {isIndividual && (
          <div className="createGameModalPage-bulkCard">
            <div className="createGameModalPage-sectionHeader">
              <h4>Match Format</h4>
              <p>Choose whether these games are singles or doubles.</p>
            </div>

            <div>
              <button
                type="button"
                onClick={() => setMatchFormat("singles")}
              >
                Singles
              </button>

              <button
                type="button"
                onClick={() => setMatchFormat("doubles")}
              >
                Doubles
              </button>
            </div>
          </div>
        )}
        <div className="createGameModalPage-gamesCard">
          <div className="createGameModalPage-sectionHeader">
            <h4>Games</h4>
            <p>
              Leave row location blank to use the default location above.
            </p>
          </div>

          <div className="createGameModalPage-rowsHeader">
            <span>{sideALabel}</span>
            <span>{sideBLabel}</span>
            <span>Time</span>
            <span>Location</span>
            <span>URL</span>
            <span></span>
          </div>

          <div className="createGameModalPage-rowList">
            {gameRows.map((row, index) => {
              const rowLocationMatches = getLocationMatches(row.location);

              return (
                <div key={row.rowKey} className="createGameModalPage-gameRow">
                  <div className="createGameModalPage-mobileRowNumber">
                    Game {index + 1}
                  </div>

                  <label className="createGameModalPage-rowField">
                    <span>{sideALabel}</span>
                    <select
                      value={row.team1_id}
                      onChange={(e) =>
                        updateRow(row.rowKey, "team1_id", e.target.value)
                      }
                    >
                      <option value="">{optionLabel}</option>
                      {participants.map((participant) => {
                        const id = getParticipantId(participant);

                        return (
                          <option key={id} value={id}>
                            {participantNameById[String(id)]}
                          </option>
                        );
                      })}
                    </select>
                  </label>


                  {isIndividual && matchFormat === "doubles" && (
                    <label className="createGameModalPage-rowField">
                      <span>Player 1 Partner</span>

                      <select
                        value={row.team1_partner_id}
                        onChange={(e) =>
                          updateRow(
                            row.rowKey,
                            "team1_partner_id",
                            e.target.value
                          )
                        }
                      >
                        <option value="">Select a player</option>

                        {players.map((participant) => {
                          const id = getParticipantId(participant);

                          return (
                            <option key={id} value={id}>
                              {participantNameById[String(id)]}
                            </option>
                          );
                        })}
                      </select>
                    </label>
                  )}




                  <label className="createGameModalPage-rowField">
                    <span>{sideBLabel}</span>
                    <select
                      value={row.team2_id}
                      onChange={(e) =>
                        updateRow(row.rowKey, "team2_id", e.target.value)
                      }
                    >
                      <option value="">{optionLabel}</option>
                      {participants.map((participant) => {
                        const id = getParticipantId(participant);

                        return (
                          <option key={id} value={id}>
                            {participantNameById[String(id)]}
                          </option>
                        );
                      })}
                    </select>
                  </label>

                  {isIndividual && matchFormat === "doubles" && (
                    <label className="createGameModalPage-rowField">
                      <span>Player 2 Partner</span>

                      <select
                        value={row.team2_partner_id}
                        onChange={(e) =>
                          updateRow(
                            row.rowKey,
                            "team2_partner_id",
                            e.target.value
                          )
                        }
                      >
                        <option value="">Select a player</option>

                        {players.map((participant) => {
                          const id = getParticipantId(participant);

                          return (
                            <option key={id} value={id}>
                              {participantNameById[String(id)]}
                            </option>
                          );
                        })}
                      </select>
                    </label>
                  )}


                  <label className="createGameModalPage-rowField">
                    <span>Time</span>
                    <input
                      type="time"
                      value={row.time}
                      onChange={(e) =>
                        updateRow(row.rowKey, "time", e.target.value)
                      }
                    />
                  </label>

                  <div className="createGameModalPage-rowField createGameModalPage-locationField">
                    <span>Location</span>

                    <div className="createGameModalPage-locationInputWrap">
                      <input
                        type="text"
                        value={row.location}
                        placeholder=""
                        onChange={(e) => {
                          updateRow(row.rowKey, "location", e.target.value);
                          updateRow(row.rowKey, "locationSearchOpen", true);
                        }}
                        onFocus={() =>
                          updateRow(row.rowKey, "locationSearchOpen", true)
                        }
                        autoComplete="off"
                      />

                      {row.locationSearchOpen && rowLocationMatches.length > 0 && (
                        <div className="createGameModalPage-locationDropdown">
                          {rowLocationMatches.map((loc) => (
                            <button
                              type="button"
                              key={loc.id}
                              className="createGameModalPage-locationDropdownItem"
                              onClick={() =>
                                handleSelectRowLocation(row.rowKey, loc)
                              }
                            >
                              {loc.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <label className="createGameModalPage-rowField">
                    <span>URL</span>
                    <input
                      type="text"
                      value={row.location_url}
                      placeholder=""
                      onChange={(e) =>
                        updateRow(row.rowKey, "location_url", e.target.value)
                      }
                    />
                  </label>

                  <button
                    type="button"
                    className="createGameModalPage-removeBtn"
                    onClick={() => removeGameRow(row.rowKey)}
                    disabled={gameRows.length === 1}
                    title="Remove game"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="createGameModalPage-footer">
          <button
            type="button"
            className="createGameModalPage-secondaryBtn"
            onClick={addAnotherGame}
          >
            + Add More Game
          </button>

          <div className="createGameModalPage-footerRight">
            <button
              type="button"
              className="createGameModalPage-cancelBtn"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>

            <button
              type="button"
              className="createGameModalPage-primaryBtn"
              onClick={handleSaveGames}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Games"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateGameModal;